import React from 'react';
import { BotIcon, FileTextIcon, HardDriveIcon, ChevronRightIcon, ClockIcon, AlertCircleIcon, BookIcon, ActivityIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export interface KnowledgeSpaceProps {
  id: string;
  name: string;
  chatbotCount: number;
  storageUsed: string;
  documentsCount: number;
  createdAt?: string;
  updatedAt?: string;
  description?: string;
  status?: 'active' | 'archived' | 'deleted';
}
export const KnowledgeSpaceCard = ({
  id,
  name,
  chatbotCount,
  storageUsed,
  documentsCount,
  updatedAt,
  description,
  status = 'active'
}: KnowledgeSpaceProps) => {
  const navigate = useNavigate();
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/space/${id}`);
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-status-success/10 border-status-success/30 text-status-success';
      case 'archived':
        return 'bg-status-warning/10 border-status-warning/30 text-status-warning';
      case 'deleted':
        return 'bg-status-error/10 border-status-error/30 text-status-error';
      default:
        return 'bg-surface border-surface-border text-gray-400';
    }
  };
  const usagePercentage = Math.min(documentsCount / 100 * 100, 100); // Example calculation
  return <button onClick={() => navigate(`/space/${id}`)} onKeyPress={handleKeyPress} className="w-full text-left group bg-surface-light rounded-none border border-surface-border p-6 
                hover:bg-surface-mid transition-colors duration-150 cursor-pointer relative overflow-hidden 
                focus:outline-none focus:ring-1 focus:ring-accent focus:ring-offset-1 focus:ring-offset-surface" aria-label={`${name} knowledge space with ${chatbotCount} ${chatbotCount === 1 ? 'chatbot' : 'chatbots'} and ${documentsCount} ${documentsCount === 1 ? 'document' : 'documents'}`} data-id="element-1359">
      <div className="space-y-6" data-id="element-1360">
        {/* Header Section - Improved spacing and alignment */}
        <div className="flex items-start justify-between gap-4" data-id="element-1361">
          <div className="flex-1 min-w-0" data-id="element-1362">
            <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1363">
              <h3 className="text-lg font-medium text-white font-mono uppercase tracking-wide group-hover:text-accent-light transition-colors" data-id="element-1364">
                {name}
              </h3>
              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-none shrink-0 ${getStatusColor(status)}`} data-id="element-1365">
                {status}
              </span>
            </div>
            {description && <p className="text-sm text-gray-400 line-clamp-2 mt-1" data-id="element-1366">
                {description}
              </p>}
          </div>
          <ChevronRightIcon size={16} className="text-gray-500 group-hover:text-accent-light group-hover:translate-x-0.5 transition-all duration-150 shrink-0 mt-1.5" aria-hidden="true" data-id="element-1367" />
        </div>
        {/* Stats Grid - Improved grid layout and consistent spacing */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1368">
          {[{
          icon: BotIcon,
          value: chatbotCount.toString().padStart(2, '0'),
          label: chatbotCount === 1 ? 'Chatbot' : 'Chatbots'
        }, {
          icon: FileTextIcon,
          value: documentsCount.toString().padStart(2, '0'),
          label: documentsCount === 1 ? 'Document' : 'Documents'
        }, {
          icon: HardDriveIcon,
          value: storageUsed,
          label: 'Storage'
        }, {
          icon: ActivityIcon,
          value: `${usagePercentage}%`,
          label: 'Usage'
        }].map((stat, index) => <div key={index} className="flex items-center gap-3" data-id="element-1369">
              <div className="flex items-center justify-center w-9 h-9 bg-surface rounded-none border border-surface-border group-hover:border-accent/30 transition-colors shrink-0" data-id="element-1370">
                <stat.icon size={16} className="text-accent-light" data-id="element-1371" />
              </div>
              <div data-id="element-1372">
                <div className="text-lg font-mono text-white leading-none mb-1" data-id="element-1373">
                  {stat.value}
                </div>
                <div className="text-xs uppercase tracking-wider text-gray-400 font-mono leading-none" data-id="element-1374">
                  {stat.label}
                </div>
              </div>
            </div>)}
        </div>
        {/* Footer - Improved alignment and spacing */}
        <div className="flex items-center justify-between pt-4 border-t border-surface-border" data-id="element-1375">
          <div className="flex items-center gap-4" data-id="element-1376">
            <div className="flex items-center gap-2" data-id="element-1377">
              <BookIcon size={14} className="text-accent-light shrink-0" data-id="element-1378" />
              <span className="font-mono text-xs uppercase text-gray-400 whitespace-nowrap" data-id="element-1379">
                {documentsCount} {documentsCount === 1 ? 'Source' : 'Sources'}
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1380">
              <AlertCircleIcon size={14} className="text-accent-light shrink-0" data-id="element-1381" />
              <span className="font-mono text-xs uppercase text-gray-400 whitespace-nowrap" data-id="element-1382">
                {status === 'active' ? 'Live' : 'Inactive'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-400 pl-4 border-l border-surface-border" data-id="element-1383">
            <ClockIcon size={14} className="shrink-0" data-id="element-1384" />
            <span className="text-xs font-mono whitespace-nowrap" data-id="element-1385">
              Updated {formatDate(updatedAt)}
            </span>
          </div>
        </div>
      </div>
    </button>;
};